import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, BookOpen, Menu, X, Leaf, LogOut, User, Settings } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import CartDrawer from "@/components/CartDrawer";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cartCount, setIsCartOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
    setMenuOpen(false);
  };

  return (
    <>
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-[#E6E2D3]" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
              <div className="w-8 h-8 bg-[#2F5233] rounded-full flex items-center justify-center group-hover:bg-[#1E3A21] transition-colors">
                <Leaf className="w-4 h-4 text-[#FDFBF7]" />
              </div>
              <div className="hidden sm:block">
                <span className="font-bold text-[#2F5233] text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Children The Organic Way
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-[#5A5A5A] hover:text-[#2F5233] font-medium transition-colors text-sm" data-testid="nav-home">Home</Link>
              <Link to="/shop" className="text-[#5A5A5A] hover:text-[#2F5233] font-medium transition-colors text-sm" data-testid="nav-shop">Shop</Link>
              {user && (
                <Link to="/my-library" className="text-[#5A5A5A] hover:text-[#2F5233] font-medium transition-colors text-sm flex items-center gap-1" data-testid="nav-library">
                  <BookOpen className="w-4 h-4" /> My Library
                </Link>
              )}
              {user?.is_admin && (
                <Link to="/admin" className="text-[#D2691E] hover:text-[#A0521A] font-medium transition-colors text-sm flex items-center gap-1" data-testid="nav-admin">
                  <Settings className="w-4 h-4" /> Admin
                </Link>
              )}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Cart */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 rounded-full hover:bg-[#E6E2D3] transition-colors"
                data-testid="cart-button"
              >
                <ShoppingCart className="w-5 h-5 text-[#2F5233]" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#D2691E] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Auth */}
              {user ? (
                <div className="hidden md:flex items-center gap-2">
                  <span className="text-sm text-[#5A5A5A]">Hi, {user.name.split(' ')[0]}</span>
                  <button onClick={handleLogout} className="flex items-center gap-1 text-sm text-[#5A5A5A] hover:text-[#2F5233] transition-colors" data-testid="logout-button">
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="hidden md:flex items-center gap-2">
                  <Link to="/login" className="text-sm font-medium text-[#2F5233] hover:text-[#1E3A21] transition-colors" data-testid="login-link">Login</Link>
                  <Link to="/register" className="bg-[#2F5233] text-[#FDFBF7] px-4 py-2 rounded-full text-sm font-bold hover:bg-[#1E3A21] transition-all hover:scale-105" data-testid="register-link">Register</Link>
                </div>
              )}

              {/* Mobile menu */}
              <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 rounded-full hover:bg-[#E6E2D3] transition-colors" data-testid="mobile-menu-button">
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <div className="md:hidden border-t border-[#E6E2D3] py-4 space-y-3" data-testid="mobile-menu">
              <Link to="/" onClick={() => setMenuOpen(false)} className="block px-4 py-2 text-[#1A1A1A] hover:text-[#2F5233] font-medium">Home</Link>
              <Link to="/shop" onClick={() => setMenuOpen(false)} className="block px-4 py-2 text-[#1A1A1A] hover:text-[#2F5233] font-medium">Shop</Link>
              {user && <Link to="/my-library" onClick={() => setMenuOpen(false)} className="block px-4 py-2 text-[#1A1A1A] hover:text-[#2F5233] font-medium">My Library</Link>}
              {user?.is_admin && <Link to="/admin" onClick={() => setMenuOpen(false)} className="block px-4 py-2 text-[#D2691E] font-medium">Admin Dashboard</Link>}
              {user ? (
                <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-[#1A1A1A] font-medium">Logout</button>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="block px-4 py-2 text-[#2F5233] font-medium">Login</Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)} className="block mx-4 text-center bg-[#2F5233] text-white px-4 py-2 rounded-full font-bold">Register</Link>
                </>
              )}
            </div>
          )}
        </div>
      </nav>
      <CartDrawer />
    </>
  );
}
